/**
 * A benchmark route (P2.0-T4): plain data naming a world and map and the steps the page's benchmark mode takes over
 * it, so the runner serves any map and the density stress world's route is one route file among others
 * (`routes/stress.json`). A step is a span with the camera on the character (`hold`), a camera pan through waypoints
 * after a settling rest at the first (`pan`), or a walk of the character in one direction until it passes a line,
 * measured over named stretches (`walk`). Points are in tiles, times in milliseconds. `parseRoute` checks a route read
 * from a file or a URL and names what is wrong, so a bad file stops before the run instead of steering the camera
 * somewhere odd.
 */

/**
 * The route format's version, carried in every report.
 */
export const ROUTE_FORMAT = 1;

/**
 * A point in tiles.
 */
export interface RoutePoint {
  readonly x: number;
  readonly y: number;
}

/**
 * A stretch of a walk measured as a row of its own: the row's id, what it expects to meet there, and the stretch along
 * the walk's axis, in tiles.
 */
export interface WalkWindow {
  readonly id: string;
  readonly note: string;
  readonly from: number;
  readonly to: number;
}

/**
 * One step of a route.
 */
export type RouteStep =
  | { readonly kind: "hold"; readonly id: string; readonly note: string; readonly measureMs: number }
  | { readonly kind: "pan"; readonly id: string; readonly note: string; readonly waypoints: readonly RoutePoint[]; readonly settleMs: number; readonly measureMs: number }
  | { readonly kind: "walk"; readonly id: string; readonly direction: "left" | "right" | "up" | "down"; readonly until: number; readonly windows: readonly WalkWindow[]; readonly stuckMs: number };

/**
 * A route: its id and format, the world and map it runs on, and its steps.
 */
export interface BenchRoute {
  readonly format: number;
  readonly id: string;
  readonly world: string;
  readonly map: string;
  readonly steps: readonly RouteStep[];
}

/**
 * Whether a value is a finite number.
 *
 * @param value The value.
 * @returns True for a finite number.
 */
function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/**
 * Reads a text field of a record.
 *
 * @param record The record.
 * @param key The field.
 * @param where Where the record sits, for the error.
 * @returns The text.
 * @throws Error when the field is not text.
 */
function text(record: Record<string, unknown>, key: string, where: string): string {
  const value = record[key];
  if (typeof value !== "string") {
    throw new Error(`${where}.${key} must be text`);
  }
  return value;
}

/**
 * Reads a time field of a record: a whole number of milliseconds from 0 to ten minutes.
 *
 * @param record The record.
 * @param key The field.
 * @param where Where the record sits, for the error.
 * @returns The milliseconds.
 * @throws Error when the field is not such a number.
 */
function millis(record: Record<string, unknown>, key: string, where: string): number {
  const value = record[key];
  if (!isNumber(value) || !Number.isInteger(value) || value < 0 || value > 600_000) {
    throw new Error(`${where}.${key} must be whole milliseconds from 0 to 600000`);
  }
  return value;
}

/**
 * Reads a tile coordinate.
 *
 * @param value The value.
 * @param where Where it sits, for the error.
 * @returns The coordinate.
 * @throws Error when it is not a finite number from 0 to 4,096.
 */
function tile(value: unknown, where: string): number {
  if (!isNumber(value) || value < 0 || value > 4096) {
    throw new Error(`${where} must be a tile coordinate from 0 to 4096`);
  }
  return value;
}

/**
 * Reads a record.
 *
 * @param value The value.
 * @param where Where it sits, for the error.
 * @returns The record.
 * @throws Error when it is not an object.
 */
function record(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${where} must be an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Reads one step.
 *
 * @param value The step as read.
 * @param where Where it sits, for the error.
 * @returns The step.
 * @throws Error naming the field when the step is malformed.
 */
function step(value: unknown, where: string): RouteStep {
  const raw = record(value, where);
  const id = text(raw, "id", where);
  const kind = raw["kind"];
  const note = typeof raw["note"] === "string" ? raw["note"] : "";
  if (kind === "hold") {
    return { kind, id, note, measureMs: millis(raw, "measureMs", where) };
  }
  if (kind === "pan") {
    const points = raw["waypoints"];
    if (!Array.isArray(points) || points.length < 1) {
      throw new Error(`${where}.waypoints must list at least one point`);
    }
    const waypoints = points.map((point, index) => {
      const at = record(point, `${where}.waypoints[${index}]`);
      return { x: tile(at["x"], `${where}.waypoints[${index}].x`), y: tile(at["y"], `${where}.waypoints[${index}].y`) };
    });
    return { kind, id, note, waypoints, settleMs: millis(raw, "settleMs", where), measureMs: millis(raw, "measureMs", where) };
  }
  if (kind === "walk") {
    const direction = raw["direction"];
    if (direction !== "left" && direction !== "right" && direction !== "up" && direction !== "down") {
      throw new Error(`${where}.direction must be left, right, up or down`);
    }
    const windows = raw["windows"];
    if (!Array.isArray(windows)) {
      throw new Error(`${where}.windows must be a list`);
    }
    return {
      kind,
      id,
      direction,
      until: tile(raw["until"], `${where}.until`),
      stuckMs: millis(raw, "stuckMs", where),
      windows: windows.map((window, index) => {
        const at = record(window, `${where}.windows[${index}]`);
        const from = tile(at["from"], `${where}.windows[${index}].from`);
        const to = tile(at["to"], `${where}.windows[${index}].to`);
        if (to <= from) {
          throw new Error(`${where}.windows[${index}] must end after it starts`);
        }
        return { id: text(at, "id", `${where}.windows[${index}]`), note: typeof at["note"] === "string" ? at["note"] : "", from, to };
      }),
    };
  }
  throw new Error(`${where}.kind must be hold, pan or walk`);
}

/**
 * Reads and checks a route.
 *
 * @param value The route as parsed from JSON.
 * @returns The route.
 * @throws Error naming the first malformed field, or when the format is not `ROUTE_FORMAT` or there are no steps.
 * @example
 * parseRoute({ format: 1, id: "plaza", world: "aethervale", map: "main", steps: [{ kind: "hold", id: "plaza", measureMs: 10000 }] });
 */
export function parseRoute(value: unknown): BenchRoute {
  const raw = record(value, "route");
  if (raw["format"] !== ROUTE_FORMAT) {
    throw new Error(`route.format must be ${ROUTE_FORMAT}`);
  }
  const steps = raw["steps"];
  if (!Array.isArray(steps) || steps.length === 0) {
    throw new Error("route.steps must list at least one step");
  }
  return { format: ROUTE_FORMAT, id: text(raw, "id", "route"), world: text(raw, "world", "route"), map: text(raw, "map", "route"), steps: steps.map((entry, index) => step(entry, `route.steps[${index}]`)) };
}

/**
 * Where a pan's camera stands a while into its measured span, in tiles: along the waypoints at a constant speed,
 * resting on the first while settling (negative time) and on the last once the span is over.
 *
 * @param waypoints The pan's waypoints.
 * @param elapsedMs Milliseconds into the measured span.
 * @param measureMs The span's length.
 * @returns The point.
 */
export function panPoint(waypoints: readonly RoutePoint[], elapsedMs: number, measureMs: number): RoutePoint {
  const first = waypoints[0] as RoutePoint;
  if (waypoints.length === 1 || measureMs <= 0) {
    return first;
  }
  const legs = waypoints.slice(1).map((point, index) => Math.hypot(point.x - (waypoints[index] as RoutePoint).x, point.y - (waypoints[index] as RoutePoint).y));
  const total = legs.reduce((sum, length) => sum + length, 0);
  let left = Math.min(1, Math.max(0, elapsedMs / measureMs)) * total;
  for (let index = 0; index < legs.length; index += 1) {
    const length = legs[index] as number;
    const from = waypoints[index] as RoutePoint;
    const to = waypoints[index + 1] as RoutePoint;
    if (left <= length || index === legs.length - 1) {
      const t = length === 0 ? 1 : Math.min(1, left / length);
      return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
    }
    left -= length;
  }
  return waypoints[waypoints.length - 1] as RoutePoint;
}
